import { useState, useRef, useEffect } from "react";
import { CalendarDays } from "lucide-react";
import { ChevronDownIcon } from "./icons";
import { getActivityImages } from "../../services/imageService";
import ActivityCard from "./ActivityCard";
import FallbackDayContent from "./FallbackDayContent";
import ActivityEditModal from "./ActivityEditModal";
import { formatDate } from "./utils";

// "showcase" look (the community post page): a day is a slim row — a thumbnail, a coloured "Day N" pill, the title and the date — that opens
// to the day's activities. The Day pill takes its colour from the day's place in the trip, round a small set of tones.
const DAY_TONES = [
  "bg-teal-50 text-teal-700",
  "bg-emerald-50 text-emerald-700",
  "bg-orange-50 text-orange-600",
  "bg-sky-50 text-sky-700",
  "bg-violet-50 text-violet-700",
  "bg-rose-50 text-rose-600",
];

const ORDINAL = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" })[n % 10] || "th"}`;
};

/** "Sunday 28th June, 2026" — or the date as it came, when it is not one a calendar can read. */
const longDate = (value) => {
  if (!value) return "";
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = iso ? new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const part = (opts) => date.toLocaleDateString("en-GB", { timeZone: "UTC", ...opts });
  return `${part({ weekday: "long" })} ${ORDINAL(date.getUTCDate())} ${part({ month: "long" })}, ${date.getUTCFullYear()}`;
};

/** The day's picture: its own, or the first activity's (the same lookup the activity card makes, so it is fetched once). */
const DayThumb = ({ day, city, travelPreferences }) => {
  const first = day.activities?.[0];
  const own = day.image?.url || null;
  const [found, setFound] = useState(null);
  useEffect(() => {
    if (own || !first) return undefined;
    let live = true;
    getActivityImages(first, city, 1, 0, travelPreferences)
      .then((images) => { if (live && images?.[0]?.url) setFound(images[0].url); })
      .catch(() => {});
    return () => { live = false; };
  }, [own, first, city, travelPreferences]);
  const url = own || found;
  return url ? (
    <img src={url} alt="" loading="lazy" className="h-14 w-24 shrink-0 rounded-lg object-cover" />
  ) : (
    <span className="h-14 w-24 shrink-0 rounded-lg bg-gradient-to-br from-teal-50 to-sky-50" aria-hidden="true" />
  );
};

const DayWiseItinerarySection = ({
  dayWiseItinerary,
  expandedDays,
  setExpandedDays,
  toggleDay,
  bookingLinks,
  tripSummary,
  localServices,
  travelPreferences,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onUpdateDay,
  originalDayWiseItinerary,
  currency = "INR",
  activeDay,
  isEditable = true,
  variant = "default"
}) => {
  const showcase = variant === "showcase";
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [editingDayIndex, setEditingDayIndex] = useState(null);
  const [dayEditValues, setDayEditValues] = useState({ title: "", overview: "" });
  const dayTitleInputRef = useRef(null);

  useEffect(() => {
    if (editingDayIndex !== null && dayTitleInputRef.current) {
      dayTitleInputRef.current.focus();
    }
  }, [editingDayIndex]);

  if (!dayWiseItinerary || dayWiseItinerary.length === 0) return null;

  const dayHasDeletedActivities = (dayIndex) => {
    if (!originalDayWiseItinerary?.[dayIndex]) return false;
    const originalCount = originalDayWiseItinerary[dayIndex]?.activities?.length || 0;
    const currentCount = dayWiseItinerary[dayIndex]?.activities?.length || 0;
    return currentCount < originalCount;
  };

  const revertDay = (dayIndex, e) => {
    e?.stopPropagation();
    if (!originalDayWiseItinerary?.[dayIndex] || !onUpdateDay) return;

    const original = originalDayWiseItinerary[dayIndex];
    onUpdateDay(dayIndex, {
      title: original.title,
      overview: original.overview,
      activities: [...original.activities]
    });
  };

  const cardHasChanges = (dayIndex, cardIndex) => {
    if (!originalDayWiseItinerary?.[dayIndex]?.activities) return false;

    const originalCard = originalDayWiseItinerary[dayIndex].activities[cardIndex];
    const currentCard = dayWiseItinerary[dayIndex]?.activities?.[cardIndex];

    if (!originalCard) return false;
    if (!currentCard) return false;

    return JSON.stringify(currentCard) !== JSON.stringify(originalCard);
  };

  const revertCard = (dayIndex, cardIndex) => {
    if (!originalDayWiseItinerary?.[dayIndex]?.activities?.[cardIndex]) return;
    if (!onUpdateDay) return;

    const originalCard = originalDayWiseItinerary[dayIndex].activities[cardIndex];
    const updatedActivities = [...dayWiseItinerary[dayIndex].activities];
    updatedActivities[cardIndex] = { ...originalCard };

    onUpdateDay(dayIndex, {
      ...dayWiseItinerary[dayIndex],
      activities: updatedActivities
    });
  };

  const startEditingDay = (dayIndex, e) => {
    e.stopPropagation();
    const day = dayWiseItinerary[dayIndex];
    setDayEditValues({ title: day.title || "", overview: day.overview || "" });
    setEditingDayIndex(dayIndex);
  };

  const saveDayEdits = () => {
    if (editingDayIndex !== null && onUpdateDay) {
      onUpdateDay(editingDayIndex, dayEditValues);
    }
    setEditingDayIndex(null);
  };

  const cancelDayEdits = () => {
    setEditingDayIndex(null);
    setDayEditValues({ title: "", overview: "" });
  };

  const handleAddActivity = (dayIndex) => {
    setSelectedDayIndex(dayIndex);
    setEditingActivity(null);
    setShowActivityModal(true);
  };

  const handleEditActivity = (dayIndex, activity, activityIndex) => {
    setSelectedDayIndex(dayIndex);
    setEditingActivity({ ...activity, activityIndex });
    setShowActivityModal(true);
  };

  const handleDeleteActivity = (dayIndex, activityIndex) => {
    if (window.confirm("Are you sure you want to delete this activity?")) {
      onDeleteActivity && onDeleteActivity(dayIndex, activityIndex);
    }
  };

  const handleSaveActivity = (activityData) => {
    if (editingActivity) {
      onEditActivity && onEditActivity(selectedDayIndex, editingActivity.activityIndex, activityData);
    } else {
      onAddActivity && onAddActivity(selectedDayIndex, activityData);
    }
    setShowActivityModal(false);
    setEditingActivity(null);
  };

  return (
    <div className={`bg-white rounded-xl sm:rounded-2xl shadow-sm mb-6 ${showcase ? "border border-gray-200/70 p-4 sm:p-6" : "p-3 sm:p-6 lg:p-8"}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
          {showcase ? (
            <CalendarDays className="h-6 w-6 text-teal-600" aria-hidden="true" />
          ) : (
            <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
          )}
          Day-wise Itinerary
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const allExpanded = {};
              dayWiseItinerary.forEach((_, idx) => { allExpanded[idx] = true; });
              setExpandedDays(allExpanded);
            }}
            className={`px-3 py-1.5 text-sm text-teal-600 rounded-lg transition-colors cursor-pointer ${showcase ? "font-semibold hover:bg-teal-50" : "hover:bg-teal-50"}`}
          >
            Expand All
          </button>
          <button
            onClick={() => setExpandedDays({})}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer ${showcase ? "font-semibold text-teal-600 hover:bg-teal-50" : "text-gray-600 hover:bg-gray-100"}`}
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className={showcase ? "space-y-3" : "space-y-4"}>
        {dayWiseItinerary.map((day, index) => (
          <div key={index} id={`day-${index}`} className={`border rounded-xl overflow-hidden ${showcase ? "border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" : "border-gray-200"}`}>
              <div
                className={`w-full flex items-center justify-between p-3 sm:p-5 text-left transition-all duration-300 ${
                  expandedDays[index] ? 'bg-teal-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
              <div
                className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 cursor-pointer"
                onClick={() => toggleDay(index)}
              >
                {showcase && (
                  <DayThumb day={day} city={day.currentCity || day.city || tripSummary?.destination || ''} travelPreferences={travelPreferences} />
                )}
                <span className={`px-4 py-2 rounded-lg font-bold text-sm whitespace-nowrap flex-shrink-0 transition-all ${
                  expandedDays[index]
                    ? 'bg-teal-600 text-white'
                    : showcase ? DAY_TONES[index % DAY_TONES.length] : 'bg-gray-100 text-gray-700'
                }`}>
                  Day {index + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900">{day.title}</h3>
                  {/* A copied-but-not-yet-dated trip has no day.date — an empty
                      <p> would still take up a line of blank space. */}
                  {day.date && <p className="text-sm text-gray-500">{showcase ? longDate(day.date) : formatDate(day.date)}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isEditable && dayHasDeletedActivities(index) && (
                  <button
                    onClick={(e) => revertDay(index, e)}
                    className="px-2.5 py-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium cursor-pointer"
                    title="Revert deleted activities"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    <span>Revert</span>
                  </button>
                )}
                {isEditable && expandedDays[index] && (
                  <button
                    onClick={(e) => startEditingDay(index, e)}
                    className="p-2 text-gray-400 cursor-pointer hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    title="Edit day"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => toggleDay(index)}
                  className="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                    expandedDays[index] ? 'rotate-180' : ''
                  }`} />
                </button>
              </div>
            </div>

            <div className={`transition-all duration-300 ${
              expandedDays[index] ? 'opacity-100' : 'max-h-0 overflow-hidden opacity-0'
            }`}>
              <div className="p-3 sm:p-5 border-t border-gray-200 bg-gray-50">
                {isEditable && editingDayIndex === index ? (
                  <div className="mb-6 p-4 bg-white rounded-lg border border-teal-200">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Day Title</label>
                        <input
                          ref={dayTitleInputRef}
                          type="text"
                          value={dayEditValues.title}
                          onChange={(e) => setDayEditValues(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Enter day title..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Day Overview</label>
                        <textarea
                          value={dayEditValues.overview}
                          onChange={(e) => setDayEditValues(prev => ({ ...prev, overview: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Enter day overview..."
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={saveDayEdits}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium cursor-pointer"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelDayEdits}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  day.overview && (
                    <p className="text-gray-600 mb-6">{day.overview}</p>
                  )
                )}

                {day.activities && day.activities.length > 0 ? (
                  <div className="space-y-1">
                    {day.activities.map((activity, actIdx) => (
                      <ActivityCard
                        key={actIdx}
                        activity={activity}
                        actIdx={actIdx}
                        isLastActivity={actIdx === day.activities.length - 1}
                        isLastDay={index === dayWiseItinerary.length - 1}
                        bookingLinks={bookingLinks}
                        tripSummary={tripSummary}
                        localServices={localServices}
                        travelPreferences={travelPreferences}
                        isEditable={isEditable}
                        onEdit={(activity, activityIndex) => handleEditActivity(index, activity, activityIndex)}
                        onDelete={(activityIndex) => handleDeleteActivity(index, activityIndex)}
                        dayDate={day.date}
                        dayCity={day.currentCity || day.city || ''}
                        hasChanges={isEditable ? cardHasChanges(index, actIdx) : false}
                        onRevert={() => revertCard(index, actIdx)}
                      />
                    ))}

                    {isEditable && (
                      <div className="flex gap-3 sm:gap-4">
                        <div className="flex flex-col items-center flex-shrink-0">
                        <div className="min-w-[65px] sm:min-w-[75px] px-2 py-2 bg-gray-200 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 pb-4">
                        <button
                          onClick={() => handleAddActivity(index)}
                          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="font-medium">Add Activity to Day {index + 1}</span>
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                ) : (
                  <FallbackDayContent
                    day={day}
                    bookingLinks={bookingLinks}
                    tripSummary={tripSummary}
                    localServices={localServices}
                  />
                )}

                {day.walkingDistance && (
                  <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <span>Walking: ~{day.walkingDistance}</span>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200 flex justify-center">
                  <button
                    onClick={() => {
                      toggleDay(index);
                      setTimeout(() => {
                        const dayElement = document.getElementById(`day-${index}`);
                        if (dayElement) {
                          const offset = 100;
                          const elementPosition = dayElement.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - offset;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                          });
                        }
                      }, 50);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-teal-600 hover:bg-white rounded-lg transition-all cursor-pointer"
                  >
                    <ChevronDownIcon className="w-4 h-4 rotate-180" />
                    <span>Collapse Day {index + 1}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ActivityEditModal
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setEditingActivity(null);
        }}
        onSave={handleSaveActivity}
        activity={editingActivity}
        dayNumber={selectedDayIndex !== null ? selectedDayIndex + 1 : 1}
        destination={tripSummary?.destination || ""}
        currency={currency}
      />
    </div>
  );
};

export default DayWiseItinerarySection;
